import { ArrowLeftRight, ChevronRight, X } from "lucide-react";
import type {
  AgentView,
  QueueItem,
  Snapshot,
  Status,
} from "../shared/contracts";
import { INTENT_LABELS, STATUS_LABELS } from "../shared/contracts";
import { avatarPalette } from "./avatar-palette";
import {
  agentWorkContext,
  availableWaitBand,
  canRequestHandoff,
  dispatchLabel,
  statusClock,
} from "./dashboard-data";
import { effectiveStatus, isOnline, presenceDescription } from "./presence";

export function ManagerStatusMark({
  status,
  text,
}: {
  status: Status;
  text?: string;
}) {
  const symbol =
    status === "available"
      ? "●"
      : status === "busy"
        ? "◕"
        : status === "wrap_up"
          ? "◫"
          : status === "unknown"
            ? "?"
            : status === "offline"
              ? "×"
              : status === "reserved"
                ? "◆"
                : "Ⅱ";
  return (
    <span className={`status-mark status-${status}`}>
      <span className="status-symbol" aria-hidden="true">
        {symbol}
      </span>
      {text ?? STATUS_LABELS[status]}
    </span>
  );
}

const elapsed = (at: number | null, now: number) => {
  if (at === null) return "Not observed";
  const seconds = Math.max(0, Math.floor((now - at) / 1000));
  return seconds < 60
    ? `${seconds}s ago`
    : seconds < 3600
      ? `${Math.floor(seconds / 60)}m ago`
      : `${Math.floor(seconds / 3600)}h ago`;
};
const dutyLabel = (agent: AgentView) => {
  const duty =
    agent.duty === "closer"
      ? "Closer"
      : agent.duty === "opener"
        ? "Opener"
        : "Other duty";
  return agent.bdrMember === true
    ? agent.duty === "other"
      ? "BDR"
      : `${duty} · BDR`
    : duty;
};

export function ManagerRosterRow({
  agent,
  snapshot,
  now,
  stale,
  selected,
  busy,
  teamName,
  onSelect,
  onTransfer,
}: {
  agent: AgentView;
  snapshot: Snapshot;
  now: number;
  stale: boolean;
  selected: boolean;
  busy: boolean;
  teamName: (id: string) => string;
  onSelect: () => void;
  onTransfer: () => void;
}) {
  const status = effectiveStatus(agent, now, stale);
  const canRequest = canRequestHandoff(agent, snapshot, now, stale);
  const fresh = !stale && status !== "unknown" && status !== "offline";
  const clock = fresh ? statusClock(agent.stateSince, now) : null;
  const waitBand = availableWaitBand(agent, now, stale);
  const timerLabel =
    status === "available"
      ? "Waiting"
      : status === "busy"
        ? "Call duration"
        : "Time in status";
  return (
    <div
      role="listitem"
      className={`roster-row dispatch-roster-row row-${status} ${selected ? "selected" : ""}`}
    >
      <button
        type="button"
        className="row-main"
        onClick={onSelect}
        aria-label={`Details for ${agent.name}, ${dispatchLabel(agent, now, stale)}`}
        aria-pressed={selected}
      >
        <span className="state-rail" aria-hidden="true" />
        <span className="agent-name-cell">
          <span
            className={`agent-avatar ${isOnline(agent, now, stale) ? `identity-avatar identity-avatar-${avatarPalette(agent.id)}` : ""}`}
            aria-hidden="true"
          >
            {agent.name
              .split(/\s+/)
              .filter(Boolean)
              .slice(0, 2)
              .map((part) => part[0])
              .join("")}
          </span>
          <span className="agent-name">
            <strong>
              {agent.name}
              {agent.id === snapshot.viewer.id && <em>You</em>}
            </strong>
            <small>
              {dutyLabel(agent)} ·{" "}
              {agent.teamIds.map(teamName).join(", ") || "No team"}
            </small>
          </span>
        </span>
        <span className="agent-activity">
          <ManagerStatusMark
            status={status}
            text={dispatchLabel(agent, now, stale)}
          />
          <small>Intent: {INTENT_LABELS[agent.intent]}</small>
        </span>
        <span
          className={`agent-observed ${fresh ? "observation-fresh" : "observation-uncertain"}`}
        >
          <span className="status-time-label">
            {clock ? timerLabel : "Activity unconfirmed"}
          </span>
          {clock ? (
            <strong
              className={`status-clock ${waitBand ? `wait-${waitBand}` : ""}`}
              aria-label={`${timerLabel} ${clock}`}
            >
              {clock}
            </strong>
          ) : (
            <small>Last known: {STATUS_LABELS[agent.status]}</small>
          )}
          {status === "busy" && clock && <small>Includes ringing</small>}
        </span>
      </button>
      <div className="row-action">
        <button
          type="button"
          className={canRequest ? "request-handoff" : "row-details"}
          disabled={canRequest && busy}
          onClick={canRequest ? onTransfer : onSelect}
          aria-label={
            canRequest
              ? `Request handoff to ${agent.name}`
              : `View details for ${agent.name}`
          }
        >
          {canRequest ? (
            <>
              Request
              <ArrowLeftRight size={15} aria-hidden="true" />
            </>
          ) : (
            <>
              Details
              <ChevronRight size={15} aria-hidden="true" />
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export function AgentDispatchDetail({
  agent,
  snapshot,
  now,
  stale,
  busy,
  teamName,
  onClose,
  onTransfer,
  onQueueItem,
}: {
  agent: AgentView;
  snapshot: Snapshot;
  now: number;
  stale: boolean;
  busy: boolean;
  teamName: (id: string) => string;
  onClose: () => void;
  onTransfer: () => void;
  onQueueItem: (item: QueueItem) => void;
}) {
  const status = effectiveStatus(agent, now, stale);
  const work = agentWorkContext(agent, snapshot, now);
  const canRequest = canRequestHandoff(agent, snapshot, now, stale);
  const peerName = (id: string) =>
    snapshot.agents.find((candidate) => candidate.id === id)?.name ?? "Agent";
  const recent = snapshot.activity
    .filter((event) => event.userId === agent.id)
    .sort((a, b) => b.createdAt - a.createdAt)
    .slice(0, 3);
  return (
    <section
      className="context-section detail-section dispatch-agent-detail"
      aria-label={`Dispatch detail for ${agent.name}`}
    >
      <div className="row-between">
        <p className="eyebrow">AGENT CONTEXT</p>
        <button
          type="button"
          className="icon-button"
          aria-label="Close agent detail"
          onClick={onClose}
        >
          <X size={17} />
        </button>
      </div>
      <h2 className="detail-name">
        {agent.name}
        {agent.id === snapshot.viewer.id ? " · You" : ""}
      </h2>
      <p className="meta">
        {dutyLabel(agent)} ·{" "}
        {agent.teamIds.map(teamName).join(", ") || "No team"}
      </p>
      <div className="detail-presence">
        <ManagerStatusMark
          status={status}
          text={dispatchLabel(agent, now, stale)}
        />
        <p>{presenceDescription(agent, now, stale)}</p>
        {status !== "offline" &&
          status !== "unknown" &&
          statusClock(agent.stateSince, now) && (
            <strong
              className={`status-clock ${availableWaitBand(agent, now, stale) ? `wait-${availableWaitBand(agent, now, stale)}` : ""}`}
            >
              {status === "available"
                ? "Waiting"
                : status === "busy"
                  ? "Call duration"
                  : "Time in status"}
              : {statusClock(agent.stateSince, now)}
            </strong>
          )}
      </div>
      <dl className="detail-facts">
        <div>
          <dt>Declared intent</dt>
          <dd>{INTENT_LABELS[agent.intent]}</dd>
        </div>
        <div>
          <dt>Last observation</dt>
          <dd>{elapsed(agent.lastSeen, now)}</dd>
        </div>
        {agent.evidence && (
          <div>
            <dt>Observed GHL tabs</dt>
            <dd>{agent.evidence.tabCount}</dd>
          </div>
        )}
      </dl>
      {status === "busy" && (
        <p className="detail-caveat">
          Ringing or active; answered state is unverified. Time shown measures
          this observed status.
        </p>
      )}
      {status === "wrap_up" && (
        <p className="detail-caveat">
          Waiting for a fresh idle screen in GHL. The agent finishes wrap-up
          there.
        </p>
      )}
      {canRequest ? (
        <button
          type="button"
          className="full-button"
          disabled={busy}
          onClick={onTransfer}
        >
          <ArrowLeftRight size={16} aria-hidden="true" />
          Request handoff
        </button>
      ) : (
        agent.id !== snapshot.viewer.id && (
          <p className="detail-caveat">
            {agent.duty === "other" && agent.role === "member"
              ? "Transfer requests require an eligible recipient."
              : "A handoff needs a fresh idle signal, Ready intent and no active reservation."}
          </p>
        )
      )}
      <div className="agent-work-summary">
        <div>
          <strong>{work.open}</strong>
          <span>open tasks</span>
        </div>
        <div>
          <strong>{work.due}</strong>
          <span>due callbacks</span>
        </div>
        <div>
          <strong>{work.handoffs.length}</strong>
          <span>active handoffs</span>
        </div>
      </div>
      <p className="meta">
        {stale
          ? "Last received work context."
          : "Saved GHL Connect work in your authorized scope."}
      </p>
      {work.handoffs.length > 0 && (
        <div className="detail-handoffs">
          <h3>Current coordination</h3>
          {work.handoffs.map((handoff) => (
            <div key={handoff.id}>
              <strong>
                {handoff.state === "pending"
                  ? "Pending request"
                  : "Accepted handoff"}
              </strong>
              <span>
                {peerName(handoff.fromUserId)} → {peerName(handoff.toUserId)}
              </span>
              <small>
                {Math.max(0, Math.ceil((handoff.expiresAt - now) / 1000))}s
                before expiry · finish transfer in GHL
              </small>
            </div>
          ))}
        </div>
      )}
      {work.next && (
        <div className="detail-next">
          <h3>Next saved callback</h3>
          <strong>{work.next.title}</strong>
          <small>
            {new Date(work.next.dueAt!).toLocaleString([], {
              dateStyle: "medium",
              timeStyle: "short",
            })}
          </small>
          <button
            type="button"
            className="text-button"
            disabled={stale || busy}
            onClick={() => onQueueItem(work.next!)}
          >
            Review task
            <ChevronRight size={15} aria-hidden="true" />
          </button>
        </div>
      )}
      {recent.length > 0 && (
        <div className="detail-activity">
          <h3>Recent desk activity</h3>
          {recent.map((event) => (
            <p key={event.id}>
              {event.message}
              <small>{elapsed(event.createdAt, now)}</small>
            </p>
          ))}
        </div>
      )}
    </section>
  );
}
